import { ImageIcon, Paperclip, X } from "lucide-react"
import { cn } from "../../lib/utils"
import type { Attachment } from "./types"

interface AttachmentStripProps {
  attachments: Attachment[]
  onRemove: (id: string) => void
  className?: string
}

/**
 * Horizontal chip row above the composer showing pending attachments.
 * Image attachments render thumbnail, others a paperclip icon. Each
 * chip carries a remove button. Uploading attachments show a faded
 * pulse to communicate in-flight state.
 */
export function AttachmentStrip({
  attachments,
  onRemove,
  className,
}: AttachmentStripProps) {
  if (attachments.length === 0) return null
  return (
    <div
      data-slot="attachment-strip"
      className={cn(
        "flex flex-wrap gap-1.5",
        className,
      )}
    >
      {attachments.map((a) => {
        const isImage = a.mimeType.startsWith("image/")
        const uploading = a.uploadStatus === "uploading"
        const errored = a.uploadStatus === "error"
        return (
          <div
            key={a.id}
            className={cn(
              "group/chip relative flex h-9 min-w-0 max-w-[12rem] items-center gap-1.5 rounded-md border border-black/10 bg-background pl-1.5 pr-1 text-xs",
              "dark:border-white/10",
              uploading && "animate-pulse",
              errored && "border-destructive/50",
            )}
          >
            {isImage ? (
              <img
                src={a.url}
                alt=""
                className="size-7 shrink-0 rounded object-cover"
              />
            ) : (
              <ImageIcon className="size-4 shrink-0 opacity-60" />
            )}
            <span className="min-w-0 truncate" title={a.filename}>
              {a.filename}
            </span>
            <button
              type="button"
              onClick={() => onRemove(a.id)}
              className="ml-0.5 grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              aria-label={`Remove ${a.filename}`}
            >
              <X className="size-3" />
            </button>
          </div>
        )
      })}
      {/* invisible icon used only to anchor a11y description */}
      <span className="sr-only">
        <Paperclip /> Attached files
      </span>
    </div>
  )
}
